import dayjs, { Dayjs, OpUnitType, QUnitType } from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isBetween from 'dayjs/plugin/isBetween';
import localeData from 'dayjs/plugin/localeData';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import weekday from 'dayjs/plugin/weekday';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import weekYear from 'dayjs/plugin/weekYear';
import 'dayjs/locale/es';
import { YEAR_MONTH_DAY_TIME_FORMAT } from '../OptimusRoutes/constants';

dayjs.extend(timezone);
dayjs.extend(utc);
dayjs.extend(relativeTime);

dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);
dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.extend(weekOfYear);
dayjs.extend(weekYear);
dayjs.extend(isBetween);

type StartAndEndOfDay = { startOfDate: Date; endOfDate: Date };

export type DayjsType = Dayjs;

export type dayjsDayjsType = dayjs.Dayjs;

export const convertToDate = (stringDate: string | Dayjs): Date => {
	return dayjs(stringDate).toDate();
};

export const stringToDayjs = (dateString: string): dayjs.Dayjs => {
	const converttedDate = dayjs(dateString);
	return converttedDate;
};

export const getFormattedDate = (date?: Date | string, format = 'YYYY-MM-DD'): Date => {
	const getDate = dayjs(date).format(format);
	return dayjs(getDate).toDate();
};

export const formatDate = (format = 'YYYY-MM-DD', normalDate?: Date | string, isUtc = false): string => {
	const date = isUtc ? dayjs.utc(normalDate).local() : dayjs(normalDate);
	const formattedDate = date.format(format);
	return formattedDate;
};

export const getTimeDifference = (
	oldDate: Date,
	newDate: Date = new Date(),
	unit: QUnitType | OpUnitType = 'hour',
	lang: string | ILocale | undefined = 'en'
): number => {
	dayjs.locale(lang);
	const getTime = dayjs(newDate);
	const result = getTime.diff(oldDate, unit);
	return result;
};

export const getCurrentDate = (): dayjs.Dayjs => {
	return dayjs();
};

export const getUtcNow = (): dayjs.Dayjs => {
	return dayjs().utc();
};

export const toUtcDate = (date: string | Date): dayjs.Dayjs => {
	return dayjs(date).utc();
};

export const getUtcStartAndEndOfDate = (
	date: string | Date,
	dateFormat: string,
	unit: dayjs.OpUnitType = 'day'
): StartAndEndOfDay => {
	const utcDate = typeof date === 'string' ? dayjs.utc(date, dateFormat) : dayjs.utc(date);
	const startOfDay = utcDate.startOf(unit).format(YEAR_MONTH_DAY_TIME_FORMAT);
	const endOfDay = utcDate.endOf(unit).format(YEAR_MONTH_DAY_TIME_FORMAT);

	return { startOfDate: new Date(startOfDay), endOfDate: new Date(endOfDay) };
};

export const getStartOfDay = (date: string): Date => {
	const dateValue = dayjs(date).startOf('day');
	const startOfDayDate = dateValue.toDate();
	return startOfDayDate;
};

export const getEndOfDay = (date: string): Date => {
	const dateValue = dayjs(date).endOf('day');
	const startOfDayDate = dateValue.toDate();
	return startOfDayDate;
};

export const getUtcOffset = (): number => {
	return dayjs().utcOffset();
};

export const utcOffsetMinutesToOffsetHours = (utcOffsetMinutes: number): string => {
	const formattedOffset = dayjs().utcOffset(utcOffsetMinutes).format('Z');
	return formattedOffset;
};

export const getElapsedTimeInUtc = (utcDate: Date): string => dayjs().to(dayjs(utcDate));

export const dateToUtc = (normalDate: Date | string, format = YEAR_MONTH_DAY_TIME_FORMAT): string => {
	const formattedDate = dayjs(normalDate).utc().format(format);
	return formattedDate;
};

export const utcToLocalTimezone = (utcDate: Date, format: string = 'YYYY-MM-DD') =>
	dayjs.utc(utcDate).local().format(format);

export const utcToLocalTime = (date: Date, format: string): dayjs.Dayjs => {
	const utcDate = dayjs.utc(date).local();
	const localTime = dayjs(utcDate, format);

	return localTime;
};

export const addUtcoffsetMinutes = (
	date: Date,
	utcOffsetMinutes: number,
	format: string = YEAR_MONTH_DAY_TIME_FORMAT,
	unit: dayjs.ManipulateType | undefined = 'minutes'
): string => dayjs.utc(date).add(utcOffsetMinutes, unit).format(format);

export const subtractUtcoffsetMinutes = (
	date: Date,
	utcOffsetMinutes: number,
	format: string = YEAR_MONTH_DAY_TIME_FORMAT,
	unit: dayjs.ManipulateType | undefined = 'minutes'
): string => dayjs.utc(date).subtract(utcOffsetMinutes, unit).format(format);

export const getDateFromNow = (
	utcDate: string,
	utcOffsetMinutes: string,
	lang: string | ILocale | undefined = 'en'
) => {
	dayjs.locale(lang);
	const formattedDate = dayjs.utc(utcDate).utcOffset(utcOffsetMinutes).fromNow();

	return formattedDate;
};

export const getTime = (): Number => dayjs().hour();

export const dateToString = (date: string | number | Date | dayjs.Dayjs | null | undefined, newFormat: string) => {
	return dayjs(date).format(newFormat);
};
